import type { View } from '../router/router';
import { apiClient } from '../services/api-client';
import { fetchStudentPaymentStatuses } from '../services/admin-metrics.service';
import { authService } from '../services/auth.service';
import { toast } from '../components/layout/toast';
import { $ } from '../utils/dom';
import {
  licenseBadgeClass,
  licenseLabel,
  paymentBadgeClass,
  paymentDisplayStatus,
  paymentStatusLabel,
  type LicenseType,
  type PaymentDisplayStatus,
  type StudentPaymentStatus,
} from '../utils/admin-labels';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface StudentRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  licenseType: LicenseType;
  isActive: boolean;
  role?: string;
  dateOfBirth?: string;
  sex?: string;
  phone?: string;
  activeRoutinesCount?: number;
}

type PaymentFilter = 'all' | 'active' | 'overdue' | 'upcoming' | 'expired' | 'none';
type LicenseFilter = 'all' | LicenseType;
type RoleFilter = 'all' | 'STUDENT' | 'COACH' | 'ADMIN';
type SexFilter = 'all' | 'M' | 'F' | 'O' | 'none';
type RoutineCountFilter = 'all' | '0' | '1' | '2' | '3' | '4' | '5plus';

// Mapeo limpio sin colisión: cada valor de filtro → PaymentDisplayStatus distinto
const PAYMENT_FILTER_MAP: Record<Exclude<PaymentFilter, 'all'>, PaymentDisplayStatus> = {
  active:   'ACTIVE',
  upcoming: 'UPCOMING',
  overdue:  'OVERDUE',
  expired:  'OVERDUE', // alias semántico para OVERDUE en la UI
  none:     'NONE',
};

export class AdminStudentsPage implements View {
  private students: StudentRow[] = [];
  private paymentMap = new Map<string, StudentPaymentStatus>();
  private loading = true;
  private errorMsg: string | null = null;
  private formMode: 'none' | 'create' = 'none';

  // Estado de filtros — vive en memoria, no en la URL
  private search = '';
  private paymentFilter: PaymentFilter = 'all';
  private licenseFilter: LicenseFilter = 'all';
  private roleFilter: RoleFilter = 'all';
  private sexFilter: SexFilter = 'all';
  private routineCountFilter: RoutineCountFilter = 'all';
  private includeInactive = false;

  async render(): Promise<string> {
    await this.loadData();
    if (this.loading) return this.renderLoading();
    if (this.errorMsg) return this.renderError();
    return this.renderPage();
  }

  private renderPage(): string {
    const isAdmin = authService.hasRole(['ADMIN']);
    const filtered = this.getFilteredStudents();
    const totalActive = this.students.filter(s => s.isActive !== false).length;
    const hasActiveFilters = this.hasActiveFilters();

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <h2 data-testid="admin-students-title">${isAdmin ? 'Usuarios del gimnasio' : 'Alumnos del gimnasio'}</h2>
          <p>${isAdmin
            ? 'Gestiona todos los usuarios, roles, licencias y acceso al detalle de cada perfil.'
            : 'Gestiona la membresía, licencias y acceso al detalle de cada alumno.'
          }</p>
        </div>
        <button id="btn-new-student" class="btn btn-primary" data-testid="btn-new-student">
          ${getTyrisIcon('plus', { size: 16 })} Registrar Usuario
        </button>
      </div>

      ${this.formMode === 'create' ? this.renderForm() : ''}

      <!-- Filtros en memoria -->
      <div class="card-glass admin-filters-bar" data-testid="admin-students-filters">
        <div class="admin-filter-col admin-filter-col--grow">
          <label class="admin-filter-label" for="student-search">Buscar</label>
            <input
              id="student-search"
              type="search"
              class="admin-input"
              placeholder="Nombre o correo..."
              value="${this.escapeHtml(this.search)}"
              data-testid="student-search-input"
              autocomplete="off"
            />
          </div>

          <div class="admin-filter-col admin-filter-col--fit">
            <label class="admin-filter-label" for="payment-filter">Pago</label>
            <select id="payment-filter" class="admin-select" data-testid="payment-filter-select">
              <option value="all"     ${this.paymentFilter === 'all'      ? 'selected' : ''}>Todos</option>
              <option value="active"  ${this.paymentFilter === 'active'   ? 'selected' : ''}>Al día</option>
              <option value="upcoming"${this.paymentFilter === 'upcoming' ? 'selected' : ''}>Próx. vencimiento</option>
              <option value="overdue" ${this.paymentFilter === 'overdue'  ? 'selected' : ''}>Cuota vencida</option>
              <option value="none"    ${this.paymentFilter === 'none'     ? 'selected' : ''}>Sin pago</option>
            </select>
          </div>

          <div class="admin-filter-col admin-filter-col--fit">
            <label class="admin-filter-label" for="role-filter">Rol</label>
            <select id="role-filter" class="admin-select" data-testid="role-filter-select">
              <option value="all"    ${this.roleFilter === 'all'     ? 'selected' : ''}>Todos</option>
              <option value="STUDENT"${this.roleFilter === 'STUDENT' ? 'selected' : ''}>Alumno</option>
              <option value="COACH"  ${this.roleFilter === 'COACH'   ? 'selected' : ''}>Profesor</option>
              <option value="ADMIN"  ${this.roleFilter === 'ADMIN'   ? 'selected' : ''}>Admin</option>
            </select>
          </div>

          <div class="admin-filter-col admin-filter-col--fit">
            <label class="admin-filter-label" for="sex-filter">Sexo</label>
            <select id="sex-filter" class="admin-select" data-testid="sex-filter-select">
              <option value="all"  ${this.sexFilter === 'all'  ? 'selected' : ''}>Todos</option>
              <option value="M"    ${this.sexFilter === 'M'    ? 'selected' : ''}>Masculino</option>
              <option value="F"    ${this.sexFilter === 'F'    ? 'selected' : ''}>Femenino</option>
              <option value="O"    ${this.sexFilter === 'O'    ? 'selected' : ''}>Otro</option>
              <option value="none" ${this.sexFilter === 'none' ? 'selected' : ''}>Sin especificar</option>
            </select>
          </div>

          <div class="admin-filter-col admin-filter-col--fit">
            <label class="admin-filter-label" for="license-filter">Licencia</label>
            <select id="license-filter" class="admin-select" data-testid="license-filter-select">
              <option value="all"              ${this.licenseFilter === 'all'               ? 'selected' : ''}>Todas</option>
              <option value="PERSONALIZED"     ${this.licenseFilter === 'PERSONALIZED'      ? 'selected' : ''}>Personalizado</option>
              <option value="SEMI_PERSONALIZED"${this.licenseFilter === 'SEMI_PERSONALIZED' ? 'selected' : ''}>Semi-personalizado</option>
              <option value="NONE"             ${this.licenseFilter === 'NONE'              ? 'selected' : ''}>Sin licencia</option>
            </select>
          </div>

          <div class="admin-filter-col admin-filter-col--fit">
            <label class="admin-filter-label" for="routine-count-filter">Rutinas vigentes</label>
            <select id="routine-count-filter" class="admin-select" data-testid="routine-count-filter-select">
              <option value="all"    ${this.routineCountFilter === 'all'    ? 'selected' : ''}>Todas</option>
              <option value="0"      ${this.routineCountFilter === '0'      ? 'selected' : ''}>Sin rutinas</option>
              <option value="1"      ${this.routineCountFilter === '1'      ? 'selected' : ''}>1 rutina</option>
              <option value="2"      ${this.routineCountFilter === '2'      ? 'selected' : ''}>2 rutinas</option>
              <option value="3"      ${this.routineCountFilter === '3'      ? 'selected' : ''}>3 rutinas</option>
              <option value="4"      ${this.routineCountFilter === '4'      ? 'selected' : ''}>4 rutinas</option>
              <option value="5plus"  ${this.routineCountFilter === '5plus'  ? 'selected' : ''}>5 o más</option>
            </select>
          </div>

          <div class="admin-filter-col admin-filter-col--auto">
            <span class="admin-filter-label">Estado</span>
            <label class="admin-check-row">
              <input type="checkbox" id="toggle-inactive" ${this.includeInactive ? 'checked' : ''} />
              Incluir bajas
            </label>
          </div>

          <!-- Botón limpiar (solo visible con filtros activos) -->
          <div class="admin-filter-col admin-filter-col--auto">
            <button
              id="btn-clear-filters"
              class="btn btn-outline btn-sm"
              style="${hasActiveFilters ? '' : 'visibility:hidden;'}"
              title="Limpiar todos los filtros"
            >
              ${getTyrisIcon('close', { size: 14 })} Limpiar filtros
            </button>
          </div>

        <!-- Contador de resultados -->
        <div id="filter-results-bar" class="admin-results-bar">
          <span id="filter-count-badge" class="badge ${hasActiveFilters ? 'badge-admin' : ''}">
            ${hasActiveFilters
              ? `${filtered.length} resultado${filtered.length !== 1 ? 's' : ''} de ${totalActive} activos`
              : `${totalActive} usuario${totalActive !== 1 ? 's' : ''} activos`
            }
          </span>
          ${hasActiveFilters ? this.renderActivePills() : ''}
        </div>
      </div>

      <!-- Tabla -->
      <div class="card-glass admin-table-wrap" data-testid="admin-students-table">
        ${filtered.length === 0
          ? `<div class="admin-empty" data-testid="admin-students-empty">
               <p>No hay usuarios que coincidan con los filtros aplicados.</p>
               <button id="btn-clear-filters-empty" class="btn btn-outline btn-sm">Limpiar filtros</button>
             </div>`
          : `<table class="admin-table">
               <thead>
                   <tr>
                     <th>Usuario</th>
                     <th>Estado</th>
                     <th>Licencia</th>
                     <th>Rutinas vigentes</th>
                     <th>Pago</th>
                     <th>Próximo vencimiento</th>
                     <th></th>
                   </tr>
               </thead>
               <tbody>
                 ${filtered.map(s => this.renderRow(s)).join('')}
               </tbody>
             </table>`
        }
      </div>
    `;
  }

  /** Píldoras visuales de filtros activos */
  private renderActivePills(): string {
    const pills: string[] = [];
    if (this.search) {
      pills.push(`<span class="filter-pill">${getTyrisIcon('search', { size: 14 })} "${this.escapeHtml(this.search)}"</span>`);
    }
    if (this.paymentFilter !== 'all') {
      const labels: Record<string, string> = { active: 'Al día', upcoming: 'Próx. vencimiento', overdue: 'Cuota vencida', none: 'Sin pago' };
      pills.push(`<span class="filter-pill">${getTyrisIcon('pagos', { size: 14 })} ${labels[this.paymentFilter] ?? this.paymentFilter}</span>`);
    }
    if (this.roleFilter !== 'all') {
      const labels: Record<string, string> = { STUDENT: 'Alumnos', COACH: 'Profesores', ADMIN: 'Admins' };
      pills.push(`<span class="filter-pill">${getTyrisIcon('perfil', { size: 14 })} ${labels[this.roleFilter]}</span>`);
    }
    if (this.sexFilter !== 'all') {
      const labels: Record<string, string> = { M: 'Masculino', F: 'Femenino', O: 'Otro', none: 'Sin especificar' };
      pills.push(`<span class="filter-pill">${getTyrisIcon('perfil', { size: 14 })} ${labels[this.sexFilter]}</span>`);
    }
    if (this.licenseFilter !== 'all') {
      const labels: Record<string, string> = { PERSONALIZED: 'Personalizado', SEMI_PERSONALIZED: 'Semi-personalizado', NONE: 'Sin licencia' };
      pills.push(`<span class="filter-pill">${getTyrisIcon('mis-rutinas', { size: 14 })} ${labels[this.licenseFilter] ?? this.licenseFilter}</span>`);
    }
    if (this.routineCountFilter !== 'all') {
      const label = this.routineCountFilter === '0'
        ? 'Sin rutinas'
        : this.routineCountFilter === '5plus'
          ? '5 o más rutinas'
          : `${this.routineCountFilter} rutina${this.routineCountFilter === '1' ? '' : 's'}`;
      pills.push(`<span class="filter-pill">${getTyrisIcon('mis-rutinas', { size: 14 })} ${label}</span>`);
    }
    if (this.includeInactive) {
      pills.push(`<span class="filter-pill">${getTyrisIcon('darse-de-baja', { size: 14 })} Incluye bajas</span>`);
    }
    return pills.join('');
  }

  afterRender(): void {
    this.bindFilterEvents();
    this.bindTableEvents();
    this.bindFormEvents();
  }

  /** Conecta todos los controles de filtro con actualización en memoria */
  private bindFilterEvents(): void {
    const searchInput    = document.getElementById('student-search')       as HTMLInputElement | null;
    const paymentSelect  = document.getElementById('payment-filter')       as HTMLSelectElement | null;
    const licenseSelect  = document.getElementById('license-filter')       as HTMLSelectElement | null;
    const roleSelect     = document.getElementById('role-filter')          as HTMLSelectElement | null;
    const sexSelect      = document.getElementById('sex-filter')           as HTMLSelectElement | null;
    const routinesSelect = document.getElementById('routine-count-filter') as HTMLSelectElement | null;
    const inactiveToggle = document.getElementById('toggle-inactive')      as HTMLInputElement | null;

    const applyFilters = () => {
      this.search             = searchInput?.value.trim().toLowerCase() ?? '';
      this.paymentFilter      = (paymentSelect?.value ?? 'all') as PaymentFilter;
      this.licenseFilter      = (licenseSelect?.value ?? 'all') as LicenseFilter;
      this.roleFilter         = (roleSelect?.value ?? 'all') as RoleFilter;
      this.sexFilter          = (sexSelect?.value ?? 'all') as SexFilter;
      this.routineCountFilter = (routinesSelect?.value ?? 'all') as RoutineCountFilter;
      this.includeInactive    = inactiveToggle?.checked ?? false;
      this.rerenderTable();
    };

    const clearFilters = () => {
      this.search = '';
      this.paymentFilter = 'all';
      this.licenseFilter = 'all';
      this.roleFilter = 'all';
      this.sexFilter = 'all';
      this.routineCountFilter = 'all';
      this.includeInactive = false;
      if (searchInput)    searchInput.value    = '';
      if (paymentSelect)  paymentSelect.value  = 'all';
      if (licenseSelect)  licenseSelect.value  = 'all';
      if (roleSelect)     roleSelect.value     = 'all';
      if (sexSelect)      sexSelect.value      = 'all';
      if (routinesSelect) routinesSelect.value = 'all';
      if (inactiveToggle) inactiveToggle.checked = false;
      this.rerenderTable();
    };

    // Búsqueda con debounce
    let searchDebounce: ReturnType<typeof setTimeout> | undefined;
    searchInput?.addEventListener('input', () => {
      clearTimeout(searchDebounce);
      searchDebounce = setTimeout(applyFilters, 200);
    });

    // Selects: inmediato
    paymentSelect?.addEventListener('change', applyFilters);
    licenseSelect?.addEventListener('change', applyFilters);
    roleSelect?.addEventListener('change', applyFilters);
    sexSelect?.addEventListener('change', applyFilters);
    routinesSelect?.addEventListener('change', applyFilters);
    inactiveToggle?.addEventListener('change', applyFilters);

    // Limpiar filtros
    document.getElementById('btn-clear-filters')?.addEventListener('click', clearFilters);
    document.getElementById('btn-clear-filters-empty')?.addEventListener('click', clearFilters);
  }

  /** Indica si hay algún filtro aplicado distinto del valor por defecto */
  private hasActiveFilters(): boolean {
    return Boolean(
      this.search ||
      this.paymentFilter !== 'all' ||
      this.licenseFilter !== 'all' ||
      this.roleFilter !== 'all' ||
      this.sexFilter !== 'all' ||
      this.routineCountFilter !== 'all' ||
      this.includeInactive,
    );
  }

  /** Re-renderiza solo la tabla y el contador sin recargar datos ni la página */
  private rerenderTable(): void {
    const filtered  = this.getFilteredStudents();
    const totalActive = this.students.filter(s => s.isActive !== false).length;
    const hasActive = this.hasActiveFilters();

    // Actualizar tabla
    const tableWrap = document.querySelector('[data-testid="admin-students-table"]');
    if (tableWrap) {
      tableWrap.innerHTML = filtered.length === 0
        ? `<div class="admin-empty" data-testid="admin-students-empty">
             <p>No hay usuarios que coincidan con los filtros aplicados.</p>
             <button id="btn-clear-filters-empty" class="btn btn-outline btn-sm">Limpiar filtros</button>
           </div>`
        : `<table class="admin-table">
             <thead><tr>
               <th>Usuario</th><th>Estado</th><th>Licencia</th>
               <th>Rutinas vigentes</th><th>Pago</th><th>Próximo vencimiento</th><th></th>
             </tr></thead>
             <tbody>${filtered.map(s => this.renderRow(s)).join('')}</tbody>
           </table>`;
      // Re-bind table events after DOM update
      this.bindTableEvents();
    }

    // Actualizar contador y píldoras
    const countBadge = document.getElementById('filter-count-badge');
    if (countBadge) {
      countBadge.className = `badge ${hasActive ? 'badge-admin' : ''}`;
      countBadge.textContent = hasActive
        ? `${filtered.length} resultado${filtered.length !== 1 ? 's' : ''} de ${totalActive} activos`
        : `${totalActive} usuario${totalActive !== 1 ? 's' : ''} activos`;
    }

    // Actualizar píldoras de filtros activos
    const resultsBar = document.getElementById('filter-results-bar');
    if (resultsBar) {
      const existing = resultsBar.querySelectorAll('.filter-pill');
      existing.forEach(p => p.remove());
      if (hasActive) {
        resultsBar.insertAdjacentHTML('beforeend', this.renderActivePills());
      }
    }

    // Mostrar/ocultar botón limpiar
    const clearBtn = document.getElementById('btn-clear-filters') as HTMLButtonElement | null;
    if (clearBtn) {
      clearBtn.style.visibility = hasActive ? 'visible' : 'hidden';
    }

    // Re-bind limpiar en estado vacío
    document.getElementById('btn-clear-filters-empty')?.addEventListener('click', () => {
      const searchInput    = document.getElementById('student-search')       as HTMLInputElement | null;
      const paymentSelect  = document.getElementById('payment-filter')       as HTMLSelectElement | null;
      const licenseSelect  = document.getElementById('license-filter')       as HTMLSelectElement | null;
      const roleSelect     = document.getElementById('role-filter')          as HTMLSelectElement | null;
      const sexSelect      = document.getElementById('sex-filter')           as HTMLSelectElement | null;
      const routinesSelect = document.getElementById('routine-count-filter') as HTMLSelectElement | null;
      const inactiveToggle = document.getElementById('toggle-inactive')      as HTMLInputElement | null;
      this.search = '';
      this.paymentFilter = 'all';
      this.licenseFilter = 'all';
      this.roleFilter = 'all';
      this.sexFilter = 'all';
      this.routineCountFilter = 'all';
      this.includeInactive = false;
      if (searchInput)    searchInput.value = '';
      if (paymentSelect)  paymentSelect.value = 'all';
      if (licenseSelect)  licenseSelect.value = 'all';
      if (roleSelect)     roleSelect.value = 'all';
      if (sexSelect)      sexSelect.value = 'all';
      if (routinesSelect) routinesSelect.value = 'all';
      if (inactiveToggle) inactiveToggle.checked = false;
      this.rerenderTable();
    });
  }

  private bindTableEvents(): void {
    document.querySelectorAll('[data-student-detail]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const id = (el as HTMLElement).getAttribute('data-student-detail');
        if (id) window.location.hash = `#/admin/student?id=${id}`;
      });
    });

    document.querySelectorAll('[data-soft-delete]').forEach((el) => {
      el.addEventListener('click', async (e) => {
        e.preventDefault();
        const id = (el as HTMLElement).getAttribute('data-soft-delete');
        if (!id) return;
        if (!confirm('¿Confirmas dar de baja a este usuario? Su historial se conserva.')) return;
        try {
          await apiClient.delete(`/api/users/${id}`);
          toast.success('Usuario dado de baja correctamente.');
          // Actualizar en memoria sin recargar
          const s = this.students.find(s => s.id === id);
          if (s) s.isActive = false;
          this.rerenderTable();
        } catch (err: any) {
          toast.error(err?.body?.message ?? 'No se pudo dar de baja al usuario.');
        }
      });
    });

    document.querySelectorAll('[data-reactivate]').forEach((el) => {
      el.addEventListener('click', async (e) => {
        e.preventDefault();
        const id = (el as HTMLElement).getAttribute('data-reactivate');
        if (!id) return;
        if (!confirm('¿Confirmas reactivar a este usuario?')) return;
        try {
          await apiClient.patch(`/api/users/${id}`, { isActive: true });
          toast.success('Usuario reactivado correctamente.');
          // Actualizar en memoria sin recargar
          const s = this.students.find(s => s.id === id);
          if (s) s.isActive = true;
          this.rerenderTable();
        } catch (err: any) {
          toast.error(err?.body?.message ?? 'No se pudo reactivar al usuario.');
        }
      });
    });
  }

  private bindFormEvents(): void {
    $('#btn-new-student')?.addEventListener('click', () => {
      this.formMode = 'create';
      // Insertar formulario antes de los filtros
      const header = document.querySelector('.dashboard-header');
      if (header && !document.querySelector('[data-testid="admin-student-form"]')) {
        header.insertAdjacentHTML('afterend', this.renderForm());
        this.bindFormSubmit();
      }
    });
  }

  private bindFormSubmit(): void {
    $('#btn-cancel-student-form')?.addEventListener('click', () => {
      document.querySelector('[data-testid="admin-student-form"]')?.remove();
      this.formMode = 'none';
    });

    $('#form-student')?.addEventListener('submit', async (e: Event) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const fd = new FormData(form);
      const email     = String(fd.get('email')     ?? '').trim();
      const password  = String(fd.get('password')  ?? '').trim();
      const firstName = String(fd.get('firstName') ?? '').trim();
      const lastName  = String(fd.get('lastName')  ?? '').trim();
      const phone     = String(fd.get('phone')     ?? '').trim() || undefined;
      const sex       = String(fd.get('sex')       ?? '').trim() || undefined;
      const dateOfBirth = String(fd.get('dateOfBirth') ?? '').trim() || undefined;
      const role      = String(fd.get('role')      ?? '').trim() || 'STUDENT';

      if (!email || !password || !firstName || !lastName) {
        toast.error('Todos los campos con * son obligatorios.');
        return;
      }
      if (password.length < 6) {
        toast.error('La contraseña debe tener al menos 6 caracteres.');
        return;
      }

      const submitBtn = document.querySelector<HTMLButtonElement>('[data-testid="student-form-submit"]');
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Registrando...'; }

      try {
        const newUser = await apiClient.post<StudentRow>('/api/auth/register', {
          email, password, firstName, lastName, role, phone, sex, dateOfBirth,
        });
        toast.success(`Usuario ${firstName} ${lastName} registrado con éxito.`);
        // Agregar en memoria y actualizar tabla sin recargar
        this.students.unshift({ ...newUser, isActive: true, licenseType: 'NONE', role });
        document.querySelector('[data-testid="admin-student-form"]')?.remove();
        this.formMode = 'none';
        this.rerenderTable();
      } catch (err: any) {
        const body = err?.body;
        const msg = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
        toast.error(msg ?? 'No se pudo registrar al usuario.');
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Registrar Usuario'; }
      }
    });
  }

  private renderRow(s: StudentRow): string {
    const isStudent = !s.role || s.role === 'STUDENT';
    const ps = this.paymentMap.get(s.id);
    const display = ps ? paymentDisplayStatus(ps) : 'NONE';
    const payClass = isStudent ? paymentBadgeClass(display) : 'badge';
    const payLabel = isStudent ? paymentStatusLabel(display) : '—';
    const licClass = isStudent ? licenseBadgeClass(s.licenseType) : 'badge';
    const licLabel = isStudent ? licenseLabel(s.licenseType) : '—';
    const routineCount = s.activeRoutinesCount ?? 0;
    const routineBadge = routineCount > 0
      ? `<span class="badge badge-success" title="${routineCount} rutina${routineCount === 1 ? '' : 's'} vigente${routineCount === 1 ? '' : 's'}">${getTyrisIcon('mis-rutinas', { size: 14 })} ${routineCount}</span>`
      : `<span class="badge badge-admin" title="Sin rutinas vigentes">${routineCount}</span>`;
    const nextDue  = isStudent && ps?.nextDueDate
      ? new Date(ps.nextDueDate).toLocaleDateString('es-ES', { timeZone: 'UTC' })
      : '—';

    const isActiveBadge = s.isActive === false
      ? `<span class="badge badge-error">Baja</span>`
      : `<span class="badge badge-success">Activo</span>`;

    const isUserAdmin = authService.hasRole(['ADMIN']);
    const reactivateBtn = s.isActive === false && isUserAdmin
      ? `<button class="btn btn-outline btn-sm" data-reactivate="${s.id}" style="color:var(--color-success,#10b981);border-color:var(--color-success,#10b981);display:inline-flex;align-items:center;gap:0.3rem;" title="Reactivar">${getTyrisIcon('comenzar', { size: 14 })} Reactivar</button>`
      : '';

    const roleBadge = s.role && s.role !== 'STUDENT'
      ? `<span class="badge badge-${s.role.toLowerCase()}" style="margin-left:0.4rem;font-size:0.65rem;padding:0.15rem 0.4rem;">${s.role}</span>`
      : '';

    return `
      <tr data-testid="student-row-${s.id}" style="${s.isActive === false ? 'opacity:0.55;' : ''}">
        <td>
          <div style="display:flex;align-items:center;gap:0.3rem;flex-wrap:wrap;">
            <span style="font-weight:600;">${s.firstName} ${s.lastName}</span>
            ${roleBadge}
          </div>
          <div class="admin-cell-muted" style="font-size:0.82rem;color:var(--text-muted);">${s.email}</div>
        </td>
        <td>${isActiveBadge}</td>
        <td><span class="${licClass}">${licLabel}</span></td>
        <td>${routineBadge}</td>
        <td><span class="${payClass}">${payLabel}</span></td>
        <td style="font-size:0.88rem;">${nextDue}</td>
        <td style="display:flex;gap:0.4rem;flex-wrap:wrap;">
          <a href="#/admin/student?id=${s.id}" class="btn btn-outline btn-sm" data-student-detail="${s.id}" data-testid="student-detail-link-${s.id}">
            Ver detalle →
          </a>
          ${s.isActive !== false ? `<button class="btn btn-outline btn-sm" data-soft-delete="${s.id}" style="color:var(--color-error,#ef4444);border-color:var(--color-error,#ef4444);display:inline-flex;align-items:center;gap:0.3rem;" title="Dar de baja">${getTyrisIcon('darse-de-baja', { size: 14 })} Baja</button>` : ''}
          ${reactivateBtn}
        </td>
      </tr>
    `;
  }

  private renderForm(): string {
    return `
      <div class="card-glass admin-form-card" data-testid="admin-student-form">
        <h3 class="admin-section-title">${getTyrisIcon('perfil', { size: 20 })} Registrar Nuevo Usuario</h3>
        <form id="form-student" novalidate>
          <div class="admin-form-grid">
            <label>Nombre *
              <input name="firstName" type="text" class="admin-input" required minlength="2" placeholder="Juan" data-testid="student-form-firstname" />
            </label>
            <label>Apellido *
              <input name="lastName" type="text" class="admin-input" required minlength="2" placeholder="Pérez" data-testid="student-form-lastname" />
            </label>
            <label>Email *
              <input name="email" type="email" class="admin-input" required placeholder="juan@email.com" data-testid="student-form-email" />
            </label>
            <label>Contraseña *
              <input name="password" type="text" class="admin-input" required minlength="6" value="gym123" data-testid="student-form-password" />
            </label>
            <label>Teléfono
              <input name="phone" type="tel" class="admin-input" placeholder="099123456" data-testid="student-form-phone" />
            </label>
            <label>Sexo
              <select name="sex" class="admin-select" data-testid="student-form-sex">
                <option value="">— Sin especificar —</option>
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
                <option value="O">Otro</option>
              </select>
            </label>
            <label>Fecha de Nacimiento
              <input name="dateOfBirth" type="date" class="admin-input" data-testid="student-form-dob" />
            </label>
            <label>Rol *
              <select name="role" class="admin-select" required data-testid="student-form-role">
                <option value="STUDENT" selected>Alumno (STUDENT)</option>
                <option value="COACH">Profesor (COACH)</option>
                <option value="ADMIN">Administrador (ADMIN)</option>
              </select>
            </label>
          </div>
          <div class="admin-form-actions">
            <button type="submit" class="btn btn-primary" data-testid="student-form-submit">Registrar Usuario</button>
            <button type="button" class="btn btn-outline" id="btn-cancel-student-form">Cancelar</button>
          </div>
        </form>
      </div>
    `;
  }

  private getFilteredStudents(): StudentRow[] {
    const collator = new Intl.Collator('es', { sensitivity: 'base' });

    return [...this.students]
      .sort((a, b) => collator.compare(
        `${a.firstName} ${a.lastName}`.trim(),
        `${b.firstName} ${b.lastName}`.trim(),
      ))
      .filter((s) => {
        // Bajas
        if (!this.includeInactive && s.isActive === false) return false;

        // Rol
        if (this.roleFilter !== 'all') {
          if ((s.role ?? 'STUDENT') !== this.roleFilter) return false;
        }

        // Sexo
        if (this.sexFilter !== 'all') {
          if (this.sexFilter === 'none') {
            if (s.sex) return false;
          } else if (s.sex !== this.sexFilter) {
            return false;
          }
        }

        // Búsqueda de texto
        if (this.search) {
          const haystack = `${s.firstName} ${s.lastName} ${s.email} ${s.role ?? ''}`.toLowerCase();
          if (!haystack.includes(this.search)) return false;
        }

        const isStudent = !s.role || s.role === 'STUDENT';

        // Licencia (solo aplica a STUDENTs)
        if (this.licenseFilter !== 'all') {
          if (!isStudent) return false;
          if (s.licenseType !== this.licenseFilter) return false;
        }

        // Cantidad de rutinas vigentes
        if (this.routineCountFilter !== 'all') {
          const count = s.activeRoutinesCount ?? 0;
          if (this.routineCountFilter === '5plus') {
            if (count < 5) return false;
          } else if (count !== Number(this.routineCountFilter)) {
            return false;
          }
        }

        // Pago (solo aplica a STUDENTs)
        if (this.paymentFilter !== 'all') {
          if (!isStudent) return false;
          const ps = this.paymentMap.get(s.id);
          const display = ps ? paymentDisplayStatus(ps) : 'NONE';
          const expected = PAYMENT_FILTER_MAP[this.paymentFilter as Exclude<PaymentFilter, 'all'>];
          if (display !== expected) return false;
        }

        return true;
      });
  }

  private async loadData(): Promise<void> {
    try {
      const isAdmin = authService.hasRole(['ADMIN']);
      if (isAdmin) {
        this.students = await apiClient.get<StudentRow[]>('/api/users?includeInactive=true');
      } else {
        this.students = await apiClient.get<StudentRow[]>('/api/users/students?includeInactive=true');
      }
      try {
        const studentsOnly = this.students.filter((s) => !s.role || s.role === 'STUDENT');
        this.paymentMap = await fetchStudentPaymentStatuses(studentsOnly);
      } catch {
        this.paymentMap = new Map();
      }
      this.errorMsg = null;
    } catch (err: unknown) {
      this.errorMsg = err instanceof Error ? err.message : 'Error al cargar usuarios';
    } finally {
      this.loading = false;
    }
  }

  private renderLoading(): string {
    return `<div class="admin-state-loading" data-testid="admin-students-loading"><p>Cargando usuarios...</p></div>`;
  }

  private renderError(): string {
    return `
      <div class="card-glass admin-state-error" data-testid="admin-students-error">
        <h3>No se pudo cargar el listado</h3>
        <p>${this.escapeHtml(this.errorMsg ?? 'Error desconocido')}</p>
        <button class="btn btn-primary" onclick="window.location.reload()">Reintentar</button>
      </div>
    `;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
